define('bindingHandlers/weekPicker',[
    'lib/lodash4',
    'lib/knockout',
    'jquery',
    'moment',
    'bindingHandlers/util/datePickerUtil',
    'lib/daterangepicker'
], function (_, ko, $, moment, datePickerUtil) {
    /**
     * The weekPicker binding handler is used to select the start day of a single week.
     *
     * The input tag will need a specific class name to differentiate it from other
     *   date range pickers that may be elsewhere on the page. A dropdown containing
     *   the date range picker will be created and appended before the end of the
     *   body. A new dropdown will be created per instance and referenced using the
     *   class of the input tag.
     *
     * # Required
     *   weekStart {Observable} - moment; the starting day of the week chosen.  This will always fall
     *   on the day of the week set as thefirst day of the week for the calendar.
     *
     * # Optional
     *   minDate {moment} - default: undefined; when defined any date before this date
     *   will be visible but will be disabled for selection
     *
     *   maxDate {moment} - default: undefined; when defined any date after this date
     *   will be visible but will be disabled for selection
     *
     *   firstDayOfWeek {number (0-6)} - default: specified by locale; when specified the calendar
     *   will start on the corresponding day of the week.  Any week selection must also start on the
     *   starting day of the week for the calendar.
     *
     *   timePicker {Booelan} - default: false; when true, it enables a
     *     dropdown selection of month and year. Leaving false will disable them.
     *
     *   shouldDisplaySelectableDates {Boolean} - default: false. If set to true
     *     calendar view displays the min date month by default if min date is provided and selected date is before min date
     *     calendar view displays the max date month by default if max date is provided and selected date is after max date
     *
     * @example
     *   Minimal example as input:
     *     <input class="classRelatedToParent" data-bind="
     *         weekPicker: {  weekStart: startDateValue } "/>
     *
     * @example
     *   Minimal example as div:
     *     <div class="someclassname" data-bind="
     *         weekPicker: { weekStart: startDateValue } ">
     *     </div>
     *
     * @example
     *   Full Example
     *     <div class="someclassname" data-bind= "
     *         weekPicker: {
     *             options: {
     *                 firstDayOfWeek: 2,
     *                 minDate: myMinimumDate,
     *                 maxDate: myMaximumDate
     *             },
     *             weekStart: defaultWeek
     *         }
     *     "></div>
     *
     */

    'use strict';

    ko.bindingHandlers.weekPicker = {
        init: function (element, valueAccessor) {
            //-------------------------------------------------------------------------
            // Setup
            //-------------------------------------------------------------------------
            var APPLY_EVENT = 'apply.daterangepicker';
            var options = {}; // options for daterangepicker
            var minDateSubscription, maxDateSubscription, firstDaySubscription, weekStartSubscription;

            // wrap values in an observable if string is provided
            var weekStart = valueAccessor().weekStart;
            if (!ko.isObservable(weekStart)) {
                weekStart = ko.observable(weekStart);
            }

            var bindingData = ko.toJS(valueAccessor());

            // set up the options for the daterangepicker
            options.showDropdowns = true;
            options.timePicker = false;
            options.singleDatePicker = true;
            if (valueAccessor().options && valueAccessor().options.shouldDisplaySelectableDates) {
                options.shouldDisplaySelectableDates = valueAccessor().options.shouldDisplaySelectableDates;
            } else {
                options.shouldDisplaySelectableDates = false;
            }
            options.locale = {
                daysOfWeek: ko.i18n.getShortDaysOfWeek(),
                monthNames: ko.i18n.getShortMonths(),
                firstDay: ko.i18n.getFirstDay()
            };

            if (bindingData.options) {
                if (valueAccessor().options.minDate) {
                    if (ko.isObservable(valueAccessor().options.minDate)) {
                        options.minDate = valueAccessor().options.minDate();
                        minDateSubscription = valueAccessor().options.minDate.subscribe(function () {
                            options.minDate = valueAccessor().options.minDate();
                            uiPicker.setOptions(options);
                            updateUI();
                        });
                    } else {
                        options.minDate = valueAccessor().options.minDate;
                    }

                }
                if (valueAccessor().options.maxDate) {
                    if (ko.isObservable(valueAccessor().options.maxDate)) {
                        options.maxDate = valueAccessor().options.maxDate();
                        maxDateSubscription = valueAccessor().options.maxDate.subscribe(function () {
                            options.maxDate = valueAccessor().options.maxDate();
                            uiPicker.setOptions(options);
                            updateUI();
                        });
                    } else {
                        options.maxDate = valueAccessor().options.maxDate;
                    }
                }
                if (valueAccessor().options.firstDayOfWeek) {
                    if (ko.isObservable(valueAccessor().options.firstDayOfWeek)) {
                        options.locale.firstDay = valueAccessor().options.firstDayOfWeek();
                        firstDaySubscription = valueAccessor().options.firstDayOfWeek.subscribe(function () {
                            options.locale.firstDay = valueAccessor().options.firstDayOfWeek();
                            uiPicker.setOptions(options);
                            updateUI();
                        });
                    } else {
                        options.locale.firstDay = valueAccessor().options.firstDayOfWeek;
                    }

                }
            }

            function getWeekStart(middleDate) {
                var date = middleDate.clone();

                /*
                 * If the selected middle day is before the firstDay of the week,
                 * we need to use the previous week's firstDay for the week start
                 * ex: firstDay is Wed, selected day is Tues July 29th, we want
                 * week start to be Wed July 23rd, not the 30th
                 */
                if (date.day() < options.locale.firstDay) {
                    date.add(-7, 'days');
                }

                return date.add(-middleDate.day() + options.locale.firstDay, 'days');
            }

            var $dateRangePicker = $(element).daterangepicker(options);

            // DateRangePicker object from daterangepicker.js lib
            var uiPicker = $(element).data('daterangepicker');

            // modify css so that the less styles will be applied
            uiPicker.container.addClass('weekpicker');

            // Install hover behavior
            uiPicker.container.on("mouseenter", 'tr', function (el) {
                $(el.target.parentElement).children(':not(.disabled):not(.in-range):not(.end-date):not(.start-date)').addClass('hover-range');
            });
            uiPicker.container.on("mouseleave", 'tr', function (el) {
                $(el.target.parentElement).children().removeClass('hover-range');
            });

            var isUpdatingUI = false;

            // Updates the widget UI
            var updateUI = function () {
                var externalValue = weekStart();

                isUpdatingUI = true;

                // these calls will trigger the picker to fire it's apply event
                // so we must set isUpdatingUI to tell the subscribe below not to fire
                uiPicker.setStartDate(moment(externalValue));

                uiPicker.setEndDate(moment(externalValue).clone().add(6, 'days'));

                isUpdatingUI = false;
            };

            // Subscription to update the ui if the valueAccessor is set to a value (for 2-way binding)
            // if the start date is updated externally or internally we need to do exactly the same thing.  We need to make sure
            // that the weekStart is actually the start day of the week for the given firstDayOfWeek
            weekStartSubscription = weekStart.subscribe(function () {
                if (isUpdatingUI) {
                    return;
                }

                if (weekStart().day() !== options.locale.firstDay) {
                    weekStart(getWeekStart(weekStart()));
                    return;
                }

                updateUI();
            });



            if (_.isUndefined(bindingData.weekStart)) {
                throw new Error("datePicker binding requires a reference to an observable or an object with a 'weekStart' attribute as its argument.");
            }

            $dateRangePicker.on(APPLY_EVENT, function (event, picker) {
                weekStart(moment(picker.startDate));
            });

            datePickerUtil.customizeCalendarHeader(uiPicker);

            datePickerUtil.handleDisposal(element, function () {
                $dateRangePicker.off(APPLY_EVENT);
                weekStartSubscription.dispose();
                if (minDateSubscription) {
                    minDateSubscription.dispose();
                }
                if (maxDateSubscription) {
                    maxDateSubscription.dispose();
                }
                if (firstDaySubscription) {
                    firstDaySubscription.dispose();
                }
            });

            // update UI initially with value stored in valueAccessor
            updateUI();
        }
    };
});

